import { Piano } from '@derstandard/piano';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type NavigateOptions, useLocation, useNavigate as useReactRouterNavigate } from 'react-router-dom';

import { Context } from '@/components/Store';
import { dst } from '@/lib/events';
import { warn as warnLog } from '@/lib/log';
import authentication from './authentication.ts';

type EventCallback = (event: Event) => void;
type MessageCallback = (event: MessageEvent) => void;

/**
 * Wrapper around react-router's useNavigate that accepts search params as a plain object.
 * Entries with null or undefined values are filtered out before being serialized.
 *
 * @example
 * const navigate = useNavigate();
 * navigate('/fehler', { searchParams: { code: 'emailInUse' } });
 * navigate('/danke', { replace: true });
 * navigate('/approve', { searchParams: { creq: '2' }, state: { from: 'payment' } });
 */
export function useNavigate() {
  const navigate = useReactRouterNavigate();

  return useCallback(
    (pathname: string, { searchParams, ...navigateOptions }: NavigateOptions & { searchParams?: Record<string, string | null | undefined> } = {}) => {
      const searchString = searchParams
        ? new URLSearchParams(Object.entries(searchParams).filter((entry): entry is [string, string] => !!entry[1])).toString()
        : '';
      navigate({ pathname, search: searchString ? `?${searchString}` : '' }, navigateOptions);
    },
    [navigate]
  );
}

export const useTokenID = (callback: EventCallback): void => {
  useEffect(() => {
    window.addEventListener(dst.user.idtokenreceived, callback);

    // remove on unmount
    return () => window.removeEventListener(dst.user.idtokenreceived, callback);
  }, [callback]);
};

export const useLoginError = (callback: EventCallback): void => {
  useEffect(() => {
    window.addEventListener(dst.user.invalidcredentials, callback);

    // remove on unmount
    return () => window.removeEventListener(dst.user.invalidcredentials, callback);
  }, [callback]);
};

export const useMessage = (callback: MessageCallback): void => {
  useEffect(() => {
    window.addEventListener('message', callback);

    // remove on unmount
    return () => window.removeEventListener('message', callback);
  }, [callback]);
};

export const useClickOnce = (callback: EventCallback): void => {
  useEffect(() => {
    window.addEventListener('click', callback, { once: true });

    // remove on unmount
    return () => window.removeEventListener('click', callback);
  }, [callback]);
};

export const useResize = (callback: EventCallback): void => {
  useEffect(() => {
    window.addEventListener('resize', callback);

    // remove on unmount
    return () => window.removeEventListener('resize', callback);
  }, [callback]);
};

export const useOnStateChanged = (): void => {
  const [state] = useContext(Context);
  const entryPageRef = useRef(state?.entryPage);

  // Keep ref updated with latest state value
  useEffect(() => {
    entryPageRef.current = state?.entryPage;
  }, [state?.entryPage]);

  const messageHandler = useCallback((e: MessageEvent) => {
    if (e.data.eventName === 'dst.auth.stateChanged')
      setTimeout(() => {
        void (async () => {
          if (await authentication.userIsLoggedIn()) return;

          // TODO: https://derstandard.atlassian.net/browse/KOALA-2537
          const target = entryPageRef.current ? entryPageRef.current : import.meta.env.VITE_APP_SUBSCRIPTIONS_PAGE_URL;

          warnLog(`Session expired, redirecting to ${target}`);
          window.location.href = target;
        })();
      }, 200);
  }, []);

  useEffect(() => {
    window.addEventListener('message', messageHandler, {
      passive: true
    });

    return () => window.removeEventListener('message', messageHandler);
  }, [messageHandler]);
};

/**
 * Hook for managing session state
 * @returns {Object} Session state object
 */
export function useSession(): { loading: boolean; session: unknown } {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<unknown>();

  /**
   * Revalidates session when login state changes
   */
  function revalidateSession(): void {
    const getSession = window.DERSTANDARD?.auth?.api?.getSession;
    if (!getSession) {
      return;
    }

    getSession()
      .then((sessionData: unknown) => {
        setSession(sessionData);
        setLoading(false);
      })
      .catch(() => {
        setSession(null);
        setLoading(false);
      });
  }

  useMessage((event: MessageEvent) => {
    // TODO evaluate whether to compare to an environment variable
    if (event.origin !== window.location.origin || event.data.eventName !== dst.auth.initialSession) {
      return;
    }

    setSession(event?.data?.payload || null);
    setLoading(false);
  });

  useEffect(() => {
    if (!window.DERSTANDARD?.auth?.api?.getSession) {
      return;
    }

    revalidateSession();
  }, []);

  useMessage((event: MessageEvent) => {
    if (event.data.eventName !== dst.auth.stateChanged) {
      return;
    }

    revalidateSession();
  });

  return { loading, session };
}

/**
 * Hook that adds the Piano script
 * @param {string[]} [ignoredRoutes] - Routes where Piano should not be initialized
 * @returns {void}
 */
export function usePiano(ignoredRoutes: string[] = []): void {
  const location = useLocation();
  const { session } = useSession();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!location.pathname?.length || isInitialized) {
      return;
    }

    if (location.pathname?.length && ignoredRoutes.includes(location.pathname)) {
      return;
    }

    // weak edition check
    const isDe = window.location.host.endsWith('.de');

    new Piano({
      applicationId: isDe ? import.meta.env.VITE_APP_PIANO_APP_ID_DE : import.meta.env.VITE_APP_PIANO_APP_ID_AT,
      cxsenseSiteId: isDe ? import.meta.env.VITE_APP_PIANO_CXENSE_SITE_ID_DE : import.meta.env.VITE_APP_PIANO_CXENSE_SITE_ID_AT,
      endpoint: true,
      isSpa: true,
      sandbox: import.meta.env.VITE_APP_PIANO_IS_SANDBOXED === 'true',
      zone: 'checkout'
    });

    setIsInitialized(true);
  }, [ignoredRoutes, isInitialized, location, session]);
}
